import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductCard from "../components/product/ProductCard";
import { filterProducts } from "../data/products";
import { useStore } from "../context/StoreContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "staples", label: "Staples", type: "category" },
  { key: "exclusives", label: "Exclusives", type: "category" },
  { key: "limited", label: "Limited", type: "tag" },
  { key: "upcycled", label: "Upcycled", type: "tag" },
  { key: "custom", label: "Custom", type: "tag" },
];

const DESCRIPTORS = {
  all: "The full catalog. Every staple, every exclusive, every restored piece.",
  staples: "Built to be worn into the ground. The pieces that anchor everything.",
  exclusives: "1-of-1s. Made once, never repeated. Reworked from materials with a past.",
  limited: "Short runs. When they're gone, they're gone.",
  upcycled: "Built from rescued cloth, vintage denim, and second lives.",
  custom: "Made with you. Reach out, we'll build it from scratch.",
};

const TITLES = {
  all: "SHOP ALL",
  staples: "STAPLES",
  exclusives: "EXCLUSIVES",
  limited: "LIMITED",
  upcycled: "UPCYCLED",
  custom: "CUSTOM",
};

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("newest");
  const { products, productsLoading } = useStore();

  const category = searchParams.get("category");
  const tag = searchParams.get("tag");

  const activeKey = category || tag || "all";

  const filtered = useMemo(() => {
    const list = filterProducts({ category, tag }, products);
    if (sort === "price-asc") return [...list].sort((a, b) => a.priceInr - b.priceInr);
    if (sort === "price-desc") return [...list].sort((a, b) => b.priceInr - a.priceInr);
    return list;
  }, [category, tag, sort, products]);

  const setFilter = (f) => {
    const next = new URLSearchParams();
    if (f.key === "all") {
      // no params
    } else if (f.type === "category") {
      next.set("category", f.key);
    } else if (f.type === "tag") {
      next.set("tag", f.key);
    }
    setSearchParams(next);
  };

  return (
    <div data-testid="page-shop" className="pt-[80px]">
      {/* header */}
      <section className="px-4 md:px-12 pt-10 md:pt-16 pb-6 max-w-[1440px] mx-auto">
        <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-4">
          HOME / SHOP / {TITLES[activeKey]}
        </p>
        <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none">
          {TITLES[activeKey]}
        </h1>
        <p className="font-body text-lg md:text-xl mt-4 text-ink/80 max-w-2xl">
          {DESCRIPTORS[activeKey]}
        </p>
      </section>

      {/* sticky filter bar */}
      <div className="sticky top-[64px] z-[80] bg-cream/85 backdrop-blur-xl">
        <div className="max-w-[1440px] mx-auto px-4 md:px-12 py-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f)}
              data-testid={`shop-filter-${f.key}`}
              className={`dc-pill ${activeKey === f.key ? "is-active" : ""}`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto flex-shrink-0">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort"
              className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] bg-ink/5 rounded-full px-4 py-2.5 outline-none cursor-pointer hover:bg-ink/10 transition-colors"
            >
              <option value="newest">NEWEST</option>
              <option value="price-asc">PRICE &uarr;</option>
              <option value="price-desc">PRICE &darr;</option>
            </select>
          </div>
        </div>
        <div className="hairline mx-4 md:mx-12" />
      </div>

      <section className="px-4 md:px-12 py-10 md:py-14 max-w-[1440px] mx-auto">
        {productsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-cream-soft rounded-[20px] shadow-soft animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="shop-empty">
            <p className="font-mono uppercase tracking-widest text-ink/70">
              NO DAMAGE FOUND.
            </p>
            <Link
              to="/shop"
              className="inline-block mt-4 font-mono text-xs uppercase tracking-widest text-ink underline underline-offset-4 hover:text-dc-red"
            >
              CLEAR FILTERS &rarr;
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ShopPage;
